import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import TealHeader from './TealHeader';
import TealUtilization from './TealUtilization';
import TealReferral from './TealReferral';
import TealMedicationAdherence from './TealMedicationAdherence';
import { useParams } from 'react-router-dom';
import TealDiseaseDetection from './TealDiseaseDetection';
import { getAuth, login, resetPassword, setDefaultTab } from '../../api/apolloApis';
import { getCodingFlowDashboard } from '../../api/codingFlowApis';
import MuiAlert from '@mui/material/Alert';
import Feedback from './Feedback';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CoralScreeners from './CoralScreeners';
import CoralQualityScreening from './CoralQualityScreening';
import CoralQualityDiabetes from './CoralQualityDiabetes';
import CoralMedicationAdherence from './CoralMedicationAdherence';
import PurpleCDDC from './PurpleCDDC';

import { TextField, Box, Button, Typography, Snackbar } from '@mui/material';
import TealQualityDiabetes from './TealQualityDiabetes';
import TealQualityScreening from './TealQualityScreening';
import TealScreeners from './TealScreeners';
import CoralHeader from './CoralHeader';
import CoralDiseaseDetection from './CoralDiseaseDetection';
import axios from 'axios';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 1 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}


const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PatientInfo = () => {
  let { pid, user } = useParams();

  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [beta, setBeta] = useState(false);
  const [resetPass, setResetPass] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [accessRole, setAccessRole] = useState(''); 
  const [hasCddcData, setHasCddcData] = useState(false); // Add state for CDDC data availability
  const [cddcDataChecked, setCddcDataChecked] = useState(false); // Track if we've checked for data
  const [state, setState] = React.useState({
    openAlert: false,
    vertical: 'top',
    horizontal: 'center',
  });

  const [widgetWidth, setWidgetWidth] = React.useState('430');

  const theme = useTheme();
  const [tabValue, setTabValue] = React.useState(Cookies.get('default_tab'));

  // Function to check if CDDC data is available for the current user
  const checkCddcDataAvailability = async () => {
    console.log('checkCddcDataAvailability called:', { authToken, user, cddcDataChecked });
    
    if (!authToken || !user || cddcDataChecked) {
      console.log('Skipping CDDC data check - missing requirements or already checked');
      return;
    }
    
    try {
      const data = await getCodingFlowDashboard({
        days: 30, // Check last 30 days
        username: user,
        token: authToken
      });
      
      // For non-admin users, filter by their username
      const filteredData = accessRole === 'admin' ? data : data.filter(item => item.provider_username === user);
      setHasCddcData(filteredData.length > 0);
    } catch (error) {
      console.log('Error checking CDDC data availability:', error);
      setHasCddcData(false);
    } finally {
      setCddcDataChecked(true);
    }
  };

  const handleTabChange = async (event, newValue) => {
    setTabValue(newValue);
    if (process.env.NODE_ENV === 'production') {
      Cookies.set('default_tab', newValue, { expires: 5, sameSite: 'None', secure: true }); 
    } else {
      Cookies.set('default_tab', newValue, { expires: 5 }); 
    }
    // Fix: pass 'user' instead of 'username' to match backend expectation
    await setDefaultTab({ username: user, token: authToken, default_tab: newValue });
    
    // Send message to parent chrome extension and update local width when CDDC tab is selected
    if (newValue === 2 && (accessRole === 'admin' || accessRole === 'pcp')) {
      // CDDC tab activated, request wider width
      console.log('Setting width to 900px for CDDC tab');
      setWidgetWidth('865');
      window.parent.postMessage("900", 'https://athenanet.athenahealth.com');
    } else {
      // Other tabs, use default width
      console.log('Setting width to 430px for other tabs');
      setWidgetWidth('415');
      window.parent.postMessage('450', 'https://athenanet.athenahealth.com');
    }
  };

  const { vertical, horizontal, openAlert } = state;

  useEffect(() => {
    const token = Cookies.get('authToken');
    const username = Cookies.get('username');
    const default_tab = Cookies.get('default_tab');

    if (token && token !== '') {
      setAuthToken(token);
      setUsername(username);
      if (default_tab && default_tab !== '') {
        setTabValue(default_tab);
      }
    }
  }, []);

  // Helper function to extract value from pandas Series or return the value directly
  const extractValue = (value) => {
    if (!value) return '';
    
    // If it's a pandas Series object (has numeric keys and dtype info)
    if (typeof value === 'object' && value !== null) {
      // Try different ways to extract the value from pandas Series
      
      // Method 1: Check if it has a '0' key (common for pandas Series)
      if ('0' in value) {
        return String(value['0']).trim();
      }
      
      // Method 2: If it's an array-like object
      if (Array.isArray(value)) {
        return String(value[0] || '').trim();
      }
      
      // Method 3: Try to parse the string representation
      const stringValue = String(value);
      if (stringValue.includes('dtype: object')) {
        // This is a pandas Series string representation
        // Extract the actual value from lines like "access_role    admin"
        const lines = stringValue.split('\n');
        for (const line of lines) {
          if (line.includes('admin') || line.includes('user') || line.includes('staff')) {
            const parts = line.split(/\s+/);
            if (parts.length >= 2) {
              return parts[parts.length - 1].trim();
            }
          }
        }
      }
      
      // Method 4: Try Object.values as fallback
      const values = Object.values(value);
      if (values.length > 0) {
        const firstValue = String(values[0]).trim();
        // Make sure we're not returning the whole object string
        if (!firstValue.includes('dtype:') && !firstValue.includes('Name:')) {
          return firstValue;
        }
      }
    }
    
    // If it's already a simple string
    if (typeof value === 'string') {
      return value.trim();
    }
    
    return String(value).trim();
  };

  useEffect(() => {
    const authenticate = async () => {
      if (authToken) {
        const username = user;
        const token = authToken;

        try {
          const auth = await getAuth({ username, token });
          if (auth.token === '') {
            Cookies.remove('authToken');
            setAuthToken('');
          } else {
            setBeta(auth.beta);
            
            // Add detailed debugging
            console.log('Auth response:', auth);
            console.log('Raw access_role:', auth.access_role);
            console.log('Type of raw access_role:', typeof auth.access_role);
            console.log('Keys of access_role:', auth.access_role ? Object.keys(auth.access_role) : 'N/A');
            console.log('Values of access_role:', auth.access_role ? Object.values(auth.access_role) : 'N/A');
            
            const role = extractValue(auth.access_role);
            setAccessRole(role);
            setTabValue(auth.default_tab);
            
            console.log('Processed access role set to:', role);
            console.log('Type of processed role:', typeof role);
            console.log('Role === "admin":', role === 'admin');
          }
        } catch (error) {
          console.error('Error during authentication:', error);
        }
      }
    };

    authenticate();
  }, [authToken, user]);

  const handleLogin = async () => {
    try {
      const data = await login({ user, password });
      if (data.token) {
        if (process.env.NODE_ENV === 'production') {
          Cookies.set('username', user, { expires: 5, sameSite: 'None', secure: true });
          Cookies.set('authToken', data.token, { expires: 5, sameSite: 'None', secure: true });
          Cookies.set('default_tab', data.default_tab, { expires: 5, sameSite: 'None', secure: true }); 
        } else {
          Cookies.set('username', user, { expires: 5 });
          Cookies.set('authToken', data.token, { expires: 5 });
          Cookies.set('default_tab', data.default_tab, { expires: 5 });
        }
        setAuthToken(data.token);
        setBeta(data.beta);
        const role = extractValue(data.access_role);
        setAccessRole(role);
        setTabValue(data.default_tab);
        // Add debugging
        console.log('Login response:', data);
        console.log('Raw access_role:', data.access_role);
        console.log('Processed access role set to:', role);
        console.log('Type of processed role:', typeof role);
        if (data.reset && data.reset === 1) {
          setResetPass(true);
          setPassword('');
        } else {
          setResetPass(false);
        }
      }
    } catch (error) {
      console.error('Login failed', error);
      setState({ ...state, openAlert: true });
    }
  };

  const handleReset = async () => {
    try {
      const data = await resetPassword({ user, password, authToken });
      if (data.token) {
        if (process.env.NODE_ENV === 'production') {
          Cookies.set('username', username, { expires: 1, sameSite: 'None', secure: true });
          Cookies.set('authToken', data.token, { expires: 1, sameSite: 'None', secure: true });
          Cookies.set('default_tab', data.default_tab, { expires: 1, sameSite: 'None', secure: true }); 
        } else {
          Cookies.set('username', username, { expires: 1 });
          Cookies.set('authToken', data.token, { expires: 1 });
          Cookies.set('default_tab', data.default_tab, { expires: 1 });
        }
        setAuthToken(data.token);
        setBeta(data.beta);
        const role = extractValue(data.access_role);
        setAccessRole(role);
        setTabValue(data.default_tab);
        if (data.reset && data.reset === 1) {
          setResetPass(true);
        } else {
          setResetPass(false);
        }
      } else {
        console.error('Login failed', data.message);
        setState({ ...state, openAlert: true });
      }
    } catch (error) {
      console.error('Login failed', error);
      setState({ ...state, openAlert: true });
    }
  };

  const handleProblem = () => {
    setIsFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
  };

  const handleLogout = () => {
    Cookies.remove('authToken');
    setAuthToken('');
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setState({ ...state, openAlert: false });
  };

  useEffect(() => {
    
    // Set initial width based on current tab value and access role
    if (tabValue === 2 && (accessRole === 'admin' || accessRole === 'pcp')) {
      console.log('Initial load: Setting width to 900px for CDDC tab');
      setWidgetWidth('865');
      window.parent.postMessage('900', 'https://athenanet.athenahealth.com');
    } else {
      console.log('Initial load: Setting width to 430px for other tabs');
      setWidgetWidth('415');
      window.parent.postMessage('450', 'https://athenanet.athenahealth.com');
    }
  }, [tabValue, accessRole]); // Run when tabValue or accessRole changes

  if (resetPass) {
    return (
      <div style={{ maxWidth: '430px', margin: '0 auto' }}>
        <Typography variant="h5">Reset Password</Typography>
        <TextField
          label="New Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
        />
        <Button variant="contained" color="primary" onClick={handleReset}>
          Set New Password
        </Button>
      </div>
    );
  }

  if (authToken !== '') {
    let module = {
      title: 'Report a problem',
      feedback_description: 'Problem Description',
      module_category: 'AppFeedback',
      module_name: 'Problems',
      module_item_key: '',
    };

    if (beta) {     
    }
    
    // Add debugging right before the return
    console.log('Current accessRole state:', accessRole);
    console.log('Should show CDDC tab:', accessRole === 'admin');
    
    return (
        <div style={{ 
          width: widgetWidth + 'px', 
          minWidth: widgetWidth + 'px',
          maxWidth: widgetWidth + 'px',
          margin: '0 auto', 
          marginBottom: '50px',
          transition: 'width 0.3s ease'
        }}>
          <AppBar position="static">
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="full width tabs"
              sx={{
                backgroundColor: '#CCCCCC',
                minHeight: '36px',
                '& .MuiTabs-flexContainer': {
                  minHeight: '36px',
                },
                '& .MuiTabs-indicator': {
                  minHeight: '4px',
                  backgroundColor: '#A5D8FF',
                },
              }}
            >
              <Tab
                label="Clinical"
                {...a11yProps(0)}
                sx={{
                  minHeight: '36px',
                  padding: '4px 8px',
                  fontSize: '0.8rem',
                  color: '#fff',
                  backgroundColor: '#00B0B0',
                  '&.Mui-selected': {
                    color: '#fff',
                    backgroundColor: '#008080',
                  },
                }}
              />
              <Tab
                label="Operations"
                {...a11yProps(1)}
                sx={{
                  minHeight: '36px',
                  padding: '4px 8px',
                  fontSize: '0.8rem',
                  color: '#fff',
                  backgroundColor: '#f8907a',
                  '&.Mui-selected': {
                    color: '#fff',
                    backgroundColor: '#b8503a',
                  },
                }}
              />
              {/* Add debugging comment and force show for testing */}
              {/* Debug: accessRole = {accessRole} */}
              {(accessRole === 'admin' || accessRole === 'pcp') && (
                <Tab
                  label="CDDC"
                  {...a11yProps(2)}
                  sx={{
                    minHeight: '36px',
                    padding: '4px 8px',
                    fontSize: '0.8rem',
                    color: '#fff',
                    backgroundColor: '#9c27b0',
                    '&.Mui-selected': {
                      color: '#fff',
                      backgroundColor: '#7b1fa2',
                    },
                  }}
                />
              )}
            </Tabs>
          </AppBar>

          <TabPanel value={tabValue} index={0} dir={theme.direction} >
            <TealHeader patientId={pid} username={user} token={authToken} />
            <TealUtilization patientId={pid} username={user} token={authToken} />
            <TealDiseaseDetection patientId={pid} username={user} token={authToken} />
            <TealScreeners patientId={pid} username={user} token={authToken} />
            <TealMedicationAdherence patientId={pid} username={user} token={authToken} />
            <TealReferral patientId={pid} username={user} token={authToken} />
            <TealQualityDiabetes patientId={pid} username={user} token={authToken} />
            <TealQualityScreening patientId={pid} username={user} token={authToken} />
          </TabPanel>

          <TabPanel value={tabValue} index={1} dir={theme.direction} >
            <CoralHeader patientId={pid} username={user} token={authToken} />
            <CoralDiseaseDetection patientId={pid} username={user} token={authToken} />
            <CoralScreeners patientId={pid} username={user} token={authToken} />
            <CoralMedicationAdherence patientId={pid} username={user} token={authToken} />
            <CoralQualityScreening patientId={pid} username={user} token={authToken} />
            <CoralQualityDiabetes patientId={pid} username={user} token={authToken} />
          </TabPanel>

          {(accessRole === 'admin' || accessRole === 'pcp') && (
            <TabPanel value={tabValue} index={2} dir={theme.direction} >
              <PurpleCDDC 
                patientId={pid} 
                username={user} 
                accessRole={accessRole}  // Make sure this line is added
              />
            </TabPanel>
          )}

          <Box display="flex" justifyContent="space-between">
            <Button variant="contained" color="primary" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </div>
      );
  }

  if (!authToken || authToken === '') {
    return (
      <div style={{ maxWidth: widgetWidth + 'px', margin: '0 auto' }}>
        <Typography variant="h5">Login</Typography>
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
        />
        <Button variant="contained" color="primary" onClick={handleLogin}>
          Login
        </Button>
        <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical, horizontal }}>
          <Alert onClose={handleClose} severity="error">
            Login failed
          </Alert>
        </Snackbar>
      </div>
    );
  }
};

export default PatientInfo;
